import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { WorkScheduleEntry } from '../../models/low-level/work-schedule-set.model';
import { WorkScheduleModel } from '../../models/low-level/work-schedule.model';
import { ConfigService } from '../config/config.service';
import { EventService } from '../event/event.service';
import { OdataWorkScheduleService } from './odata-work-schedule.service';

@Injectable({
    providedIn: 'root',
})
export class WorkScheduleService implements InitializableInterface {
    private models: WorkScheduleEntry[] = [];
    private previousDateLimit: moment.Moment;

    public isInitialized = false;
    public constructor(
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly odataWorkScheduleService: OdataWorkScheduleService,
        private readonly configService: ConfigService,
        private readonly eventService: EventService,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    public init(): Observable<WorkScheduleEntry[]> {
        if (this.clientManager.isNgmAP0()) {
            const showPreviousTimesheetDays = this.configService.getPreviousDaysTimesheetLimit();
            this.previousDateLimit = moment().subtract(showPreviousTimesheetDays, 'days');

            return this.odataWorkScheduleService.getWorkScheduleEntries().pipe(
                tap((models) => {
                    this.models = models;
                    this.isInitialized = true;
                }),
            );
        }

        this.isInitialized = true;
        return of(this.models);
    }

    public getWorkScheduleByDate(date: moment.Moment): WorkScheduleModel {
        if (!this.isInitialized) {
            throw new Error(
                `WorkScheduleService: getWorkScheduleByDate: Trying to call WorkScheduleService before initialization!`,
            );
        }

        if (!this.clientManager.isNgmAP0()) {
            throw new Error(`Trying to call WorkScheduleService in non-AA environment!`);
        }

        if (date.isBefore(this.previousDateLimit, 'days')) {
            this.log.debug(
                `Date ${date.toString()} is before previous days limit ${this.configService.getPreviousDaysTimesheetLimit()}`,
            );
        }

        let startWork: moment.Moment;
        let endWork: moment.Moment;
        let attendanceType: ConfigConstants.AttendanceType;
        // WorkSchedule service gets initialised first then TimeSheet service
        // That is why we are reading it directly from config service
        const displayNonProductiveHours = this.configService.getDisplayNonProductiveHours();

        // Do not show error message if country does not need to show non productive hours
        // this is case for Australia, New Zealand and China where countries do not depend on time sheet
        // they want the hours reported and screen where they can view it.
        if (!this.models.length && displayNonProductiveHours) {
            this.log.error(
                `Could not find any work schedule entries for ${date.format(
                    this.clientManager.getDateFormat(),
                )}; will return a free day.`,
            );
        }

        const scheduleEntryWork = this.models.find(
            (scheduleEntry) =>
                scheduleEntry.workStart.isSame(date, 'day') &&
                scheduleEntry.attendenceType === ConfigConstants.AttendanceType.WORK,
        );

        // For countries which do not show non productive hours the scheduleEntryWork will be undefined always
        // reason is from gateway we force the ScheduleEntries to be empty, so we consider any day as AttendanceType.FREE day
        if (scheduleEntryWork) {
            startWork = scheduleEntryWork.workStart.clone();
            endWork = scheduleEntryWork.workEnd.clone();
            attendanceType = ConfigConstants.AttendanceType.WORK;
        } else {
            attendanceType = ConfigConstants.AttendanceType.FREE;
            // If there is no work schedule for a day, startWork and endWork are both set to the start of the day so we
            // don't have to close a gap between the entries.
            startWork = date.clone().startOf('day');
            endWork = date.clone().startOf('day');
        }

        return new WorkScheduleModel(startWork, endWork, attendanceType);
    }
}
